"use client";

import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@tanstack/react-router";
import { AlertTriangleIcon, ClockIcon, GaugeIcon } from "lucide-react";

export type ExpiringLot = {
  id: string;
  lotNo: string;
  itemName: string;
  branchName?: string;
  expiryDate?: string;
  daysUntilExpiry?: number | null;
  qtyRemaining: number;
  valueLak: number;
};

export type PendingApproval = {
  id: string;
  type: "topup" | "refund";
  amountLak: number;
  customerName?: string;
  requestedAt: string;
};

export type LowStockAlertItem = {
  itemId: string;
  itemName: string;
  branchCode: string;
  onHand: number;
  minQty: number;
  unit: string;
};

export type DashboardAlertsProps = {
  expiringLots?: ExpiringLot[];
  pendingApprovals?: PendingApproval[];
  pendingTopupCount?: number;
  pendingRefundCount?: number;
  lowStockItemCount?: number;
  lowStockItems?: LowStockAlertItem[];
  isLoading?: boolean;
};

export function DashboardAlerts({
  expiringLots,
  pendingApprovals,
  pendingTopupCount = 0,
  pendingRefundCount = 0,
  lowStockItemCount = 0,
  lowStockItems,
  isLoading,
}: DashboardAlertsProps) {
  if (isLoading) {
    return (
      <div className="grid gap-4 sm:grid-cols-1 lg:grid-cols-2">
        {[1, 2].map((i) => (
          <Card key={i} className="rounded-xl border bg-card">
            <CardContent className="">
              <div className="mb-4 h-6 w-32 animate-pulse rounded bg-muted" />
              <div className="space-y-3">
                {[1, 2, 3].map((j) => (
                  <div
                    key={j}
                    className="h-16 animate-pulse rounded bg-muted"
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const hasExpiringLots = expiringLots && expiringLots.length > 0;
  const hasPendingApprovals =
    (pendingApprovals && pendingApprovals.length > 0) ||
    pendingTopupCount > 0 ||
    pendingRefundCount > 0;
  const hasLowStock =
    lowStockItemCount > 0 && lowStockItems && lowStockItems.length > 0;

  if (!hasExpiringLots && !hasPendingApprovals && !hasLowStock) {
    return null;
  }

  return (
    <div className="grid gap-4 sm:grid-cols-1 lg:grid-cols-2">
      {hasLowStock && (
        <Card className="rounded-xl border border-amber-500/20 bg-card">
          <CardContent className="">
            <div className="mb-4 flex items-center gap-2">
              <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-amber-500/10">
                <GaugeIcon className="size-4 text-amber-600" />
              </div>
              <h3 className="font-semibold text-lg">ສະຕ໋ອກຕ່ຳກວ່າເກັນໄຂ</h3>
              <Link
                to="/app/reports/inventory/low-stock"
                className="ml-auto rounded-full bg-amber-500/10 px-2 py-1 font-medium text-amber-700 text-xs hover:underline"
              >
                {lowStockItemCount} ລາຍການ
              </Link>
            </div>
            <div className="space-y-2">
              {lowStockItems!.slice(0, 5).map((item) => (
                <div
                  key={`${item.branchCode}:${item.itemId}`}
                  className="flex items-start justify-between rounded-lg border border-amber-500/20 bg-amber-500/5 p-3"
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm">{item.itemName}</div>
                    <div className="text-muted-foreground text-xs">
                      {item.branchCode}
                    </div>
                  </div>
                  <div className="ml-4 text-right text-sm tabular-nums">
                    {item.onHand.toLocaleString("lo-LA", {
                      maximumFractionDigits: 2,
                    })}{" "}
                    / {item.minQty.toLocaleString("lo-LA")} {item.unit}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Expiring Lots Alert */}
      {hasExpiringLots && (
        <Card className="rounded-xl border border-yellow-500/20 bg-card">
          <CardContent className="">
            <div className="mb-4 flex items-center gap-2">
              <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-yellow-500/10">
                <AlertTriangleIcon className="size-4 text-yellow-600" />
              </div>
              <h3 className="font-semibold text-lg">ສະຕ໋ອກໃກ້ຫມົດອາຍຸ</h3>
              <span className="ml-auto rounded-full bg-yellow-500/10 px-2 py-1 font-medium text-xs text-yellow-600">
                {expiringLots.length} ລາຍການ
              </span>
            </div>
            <div className="space-y-2">
              {expiringLots.slice(0, 5).map((lot) => (
                <div
                  key={lot.id}
                  className="flex items-start justify-between rounded-lg border border-yellow-500/20 bg-yellow-500/5 p-3"
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm">{lot.itemName}</div>
                    <div className="text-muted-foreground text-xs">
                      Lot: {lot.lotNo}
                      {lot.branchName && ` • ${lot.branchName}`}
                    </div>
                    {lot.daysUntilExpiry !== null &&
                      lot.daysUntilExpiry !== undefined && (
                        <div
                          className={`mt-1 font-medium text-xs ${
                            lot.daysUntilExpiry <= 7
                              ? "text-red-600"
                              : lot.daysUntilExpiry <= 30
                                ? "text-yellow-600"
                                : "text-muted-foreground"
                          }`}
                        >
                          ເຫຼືອ {lot.daysUntilExpiry} ວັນ
                        </div>
                      )}
                  </div>
                  <div className="ml-4 text-right">
                    <div className="text-muted-foreground text-xs">
                      {lot.qtyRemaining.toLocaleString("lo-LA", {
                        maximumFractionDigits: 2,
                      })}
                    </div>
                    <div className="font-medium text-sm tabular-nums">
                      {lot.valueLak.toLocaleString("lo-LA")} LAK
                    </div>
                  </div>
                </div>
              ))}
              {expiringLots.length > 5 && (
                <div className="text-center text-muted-foreground text-sm">
                  + {expiringLots.length - 5} ລາຍການເພີ່ມເຕີມ
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pending Approvals Alert */}
      {hasPendingApprovals && (
        <Card className="rounded-xl border border-blue-500/20 bg-card">
          <CardContent className="">
            <div className="mb-4 flex items-center gap-2">
              <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-blue-500/10">
                <ClockIcon className="size-4 text-blue-600" />
              </div>
              <h3 className="font-semibold text-lg">Top-up/Refund ລໍຖ້າອນຸມັດ</h3>
              <span className="ml-auto rounded-full bg-blue-500/10 px-2 py-1 font-medium text-blue-600 text-xs">
                {(pendingApprovals?.length || 0) +
                  pendingTopupCount +
                  pendingRefundCount}{" "}
                ລາຍການ
              </span>
            </div>
            <div className="space-y-2">
              {/* Show individual pending approvals if available */}
              {pendingApprovals && pendingApprovals.length > 0 ? (
                <>
                  {pendingApprovals.slice(0, 5).map((approval) => (
                    <div
                      key={approval.id}
                      className="flex items-start justify-between rounded-lg border border-blue-500/20 bg-blue-500/5 p-3"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-sm">
                          {approval.type === "topup" ? "Top-up" : "Refund"}
                        </div>
                        {approval.customerName && (
                          <div className="text-muted-foreground text-xs">
                            {approval.customerName}
                          </div>
                        )}
                        <div className="mt-1 text-muted-foreground text-xs">
                          {new Date(approval.requestedAt).toLocaleDateString(
                            "lo-LA",
                          )}
                        </div>
                      </div>
                      <div className="ml-4 text-right">
                        <div className="font-semibold text-sm tabular-nums">
                          {approval.amountLak.toLocaleString("lo-LA")} LAK
                        </div>
                      </div>
                    </div>
                  ))}
                  {pendingApprovals.length > 5 && (
                    <div className="text-center text-muted-foreground text-sm">
                      + {pendingApprovals.length - 5} ລາຍການເພີ່ມເຕີມ
                    </div>
                  )}
                </>
              ) : (
                /* Show counts if individual items not available */
                <>
                  {pendingTopupCount > 0 && (
                    <div className="rounded-lg border border-blue-500/20 bg-blue-500/5 p-3">
                      <div className="font-medium text-sm">Top-up</div>
                      <div className="text-muted-foreground text-xs">
                        {pendingTopupCount.toLocaleString("lo-LA")}{" "}
                        ລາຍການລໍຖ້າອນຸມັດ
                      </div>
                    </div>
                  )}
                  {pendingRefundCount > 0 && (
                    <div className="rounded-lg border border-blue-500/20 bg-blue-500/5 p-3">
                      <div className="font-medium text-sm">Refund</div>
                      <div className="text-muted-foreground text-xs">
                        {pendingRefundCount.toLocaleString("lo-LA")}{" "}
                        ລາຍການລໍຖ້າອນຸມັດ
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
